import { useMemo, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import { compact, partition } from "lodash"
import { FilterList } from "@mui/icons-material"
import SearchableMenu from "components/searchable-menu"
import { useIsOpPro } from "resources/account"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useTrackDashboardModuleAdded } from "connected-components/dashboard/analytics"
import { useAddModule } from "connected-components/dashboard/hooks/use-add-module"
import {
  buildSecondaryModuleConfigFromMenuItem,
  SECONDARY_MODULE_CONFIG_VIEW_KEYS,
} from "connected-components/dashboard/helpers/module-renderers"
import { MODULE_TYPES } from "connected-components/dashboard/modules/types"
import { buildContentSetViewItems, getOpenInNewTabMergeOptions, mergeItemsWithOrderAndSecondaryAction } from "./assets-menu-items"
import { useFactorsViewData } from "connected-components/factors-view/hooks/use-factors-view-data"

// TODO: Replace onViewSelected with the proper Analytics context
export default function FactorsViewSelector({ contentSets, onViewSelected, disablePortal = false }) {
  const { view, setView, filter, selectedMode } = useFactorsViewData()
  const addModule = useAddModule()
  const trackModuleAdded = useTrackDashboardModuleAdded()
  const isOpPro = useIsOpPro()

  const getLabel = useCallback((itemLabel) => {
    return filter ? <>
      <span style={{ marginRight: "0.5rem" }}><FilterList /></span>
      <span>{itemLabel}</span>
    </> : itemLabel
  }, [filter])

  const isRebalanced = selectedMode === "rebalanced"
  const [proprietaryContentSets, licensedContentSets] = useMemo(() => partition(contentSets, "proprietary"), [contentSets])
  const defaultMenuItems = useMemo(() => compact([
    {
      id: "performance",
      label: "Performance",
      groupId: "riskModel",
      groupLabel: "Risk Model",
      disabled: isRebalanced,
      tooltip: isRebalanced ? "Factor performance is not available for rebalanced position sets." : undefined,
    }, !isOpPro && {
      id: "risk",
      label: "Risk",
      groupId: "riskModel",
      groupLabel: "Risk Model",
    }, {
      id: "beta",
      label: "Beta",
      groupId: "riskModel",
      groupLabel: "Risk Model",
    },
  ]), [isOpPro, isRebalanced])

  const contentSetMenuItems = useMemo(
    () => buildContentSetViewItems(licensedContentSets, proprietaryContentSets),
    [licensedContentSets, proprietaryContentSets],
  )

  const rawMenuItems = useMemo(() => [...defaultMenuItems, ...contentSetMenuItems], [defaultMenuItems, contentSetMenuItems])

  const handleOpenInNewTab = useCallback((item, event) => {
    if (item.disabled) return
    onViewSelected?.(item.id)
    const config = buildSecondaryModuleConfigFromMenuItem(item, event, SECONDARY_MODULE_CONFIG_VIEW_KEYS.INITIAL_VIEW)
    addModule({
      type: MODULE_TYPES.FACTORS,
      config,
    })
    trackModuleAdded(MODULE_TYPES.FACTORS, config)
  }, [onViewSelected, addModule, trackModuleAdded])

  const menuItems = useMemo(() =>
    mergeItemsWithOrderAndSecondaryAction([rawMenuItems], getOpenInNewTabMergeOptions(handleOpenInNewTab)), [rawMenuItems, handleOpenInNewTab])

  useEffect(() => {
    if (menuItems.some(item => item.id === view && item.disabled)) {
      setView(menuItems.find(item => !item.disabled).id)
    }
  }, [menuItems, view, setView])

  const handleViewSelect = useCallback((selectedItem) => {
    if (selectedItem.onSelect) {
      selectedItem.onSelect()
    }
    if (selectedItem.disabled) {
      return
    }
    onViewSelected?.(selectedItem.id)
    setView(selectedItem.id)
  }, [onViewSelected, setView])

  return (
    <SearchableMenu
      items={menuItems}
      current={view}
      onSelect={handleViewSelect}
      getLabel={getLabel}
      disablePortal={disablePortal}
    />
  )
}

FactorsViewSelector.propTypes = {
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  onViewSelected: PropTypes.func,
  // Render the menu inline rather than in a portal — lets Storybook query menu items within the canvas.
  disablePortal: PropTypes.bool,
}
